import { Col, Popconfirm } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from 'src/components/app/use-page-title';
import { getContactItemById } from 'shared/store/selectors/contact.selector';
import { formatFullName } from 'shared/utils';
import { ProfileLayoutComponent } from 'src/components/profiles/profile-layout.component';
import styles from './contact-preview.module.scss';
import { Button, IconButton } from 'src/components/design-system';
import { getLang } from 'shared/store/selectors/lang.selector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMessages, faPager, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import {
  colorPalette,
  CONTACT_TYPES,
  ENTITY_TYPES,
  INTERNAL_ROUTING,
  ROLES_DOCTOR,
  ROUTES,
} from 'shared/constants';
import _ from 'lodash';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { Loader } from 'src/components/core';
import classnames from 'classnames';
import { UserAvatar } from 'src/components/avatar';
import { ProfessionalProfileManager } from 'src/components/profiles/professional-profile-manager';
import { deleteContact } from 'shared/store/actions/contacts.actions';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants/analytics';
import { getUser } from 'shared/store/selectors/user.selector';
import { ENTITY } from 'src/components/user-list/use-columns.hooks';
import { useEffect } from 'react';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { Link } from 'react-router-dom';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';

const ContactPreviewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const contact = useSelector(({ contacts, usersManagement }) =>
    getContactItemById({ contacts, usersManagement }, id),
  );

  const dispatch = useDispatch();
  const lang = useSelector(getLang('USER_PROFILE'));
  const contactsLang = useSelector(getLang('CONTACT_SCREEN'));
  const { getValueFromLang, getMultipleValuesFromLang, getLocationsValues } = useProfileLang();
  const { id: userId } = useSelector(getUser);

  const fullName = formatFullName(contact, {
    labelInParenthesis: contact?.isDeactivated ? lang.DEACTIVATED : null,
  });

  const { updateSubtitle } = usePageTitle();

  useEffect(() => {
    updateSubtitle(fullName);
  }, [fullName, updateSubtitle]);

  const canEditDetails = useSelector(isAccountAdmin);
  const isUser = contact?.isUser;
  const canEditResourcesAndPreferences = canEditDetails && isUser;

  const onEdit = () => {
    navigate(ROUTES.CONTACTS_EDIT.replace(':id', id));
    analyticsService.track(EVENTS.CONTACT.EDIT, {
      entityType: isUser ? ENTITY.USER : ENTITY.CONTACT,
      contactId: id,
      editedBy: userId,
    });
  };

  useEffect(() => {
    if (!contact?.id) return;

    const currentContextId = searchContextIdManager.getContextId();
    analyticsService.track(EVENTS.CONTENT.OPEN, {
      id: contact.id,
      contextId: currentContextId,
      entityType: ENTITY_TYPES.contacts,
    });
    analyticsService.track(EVENTS.CONTACT.OPEN_DETAILS, {
      id: contact.id,
      contextId: currentContextId,
    });
  }, [contact?.id]);

  const goToMyOrgPage = () => {
    navigate(ROUTES.CONTACTS);
  };

  const onDeleteUser = async () => {
    const searchParams = new URLSearchParams({ [INTERNAL_ROUTING.QUERY_PARAMS.USERS]: id });
    navigate(`${ROUTES.USERS_MANAGEMENT}?${searchParams.toString()}`);
  };

  const onDeleteContact = async () => {
    await dispatch(deleteContact({ id }));
    goToMyOrgPage();
  };

  const onDelete = async () => {
    if (isUser) {
      await onDeleteUser({ id });
    } else {
      await onDeleteContact({ id });
    }
  };

  const popconfirmProps = useAccessiblePopconfirm({
    onConfirm: onDelete,
  });

  const mainSection = [
    {
      name: 'email',
      label: lang.EMAIL,
      value: contact?.email,
      actions: [
        {
          icon: faEnvelope,
          label: `Send ${lang.EMAIL}`,
          onClick: () => window.open(`mailto:${contact?.email}`),
        },
      ],
      render: getValueFromLang,
    },
    {
      name: 'phone',
      label: lang.PHONE,
      value: contact?.phone,
      actions: [
        {
          icon: faMessages,
          label: lang.SEND_SMS,
          onClick: () => window.open(`sms:${contact?.phone}`),
        },
        {
          icon: faPhone,
          label: lang.CALL,
          onClick: () => window.open(`tel:${contact?.phone}`),
        },
      ],
      render: getValueFromLang,
    },
    {
      name: 'pager',
      label: lang.PAGER,
      value: contact?.pager,
      actions: [
        {
          icon: faPager,
          label: lang.CALL,
          onClick: () => window.open(`tel:${contact?.pager}`),
        },
      ],
      render: getValueFromLang,
    },
    {
      name: 'cisco',
      label: lang.CISCO,
      value: contact?.cisco,
      actions: [
        {
          icon: faMessages,
          label: lang.SEND_SMS,
          onClick: () => window.open(`sms:${contact?.cisco}`),
        },
        {
          icon: faPhone,
          label: lang.CALL,
          onClick: () => window.open(`tel:${contact?.cisco}`),
        },
      ],
      render: getValueFromLang,
    },
    {
      name: 'profession',
      label: lang.PROFESSION,
      value: contact?.profession,
      render: getValueFromLang,
      hide: !isUser,
    },
    {
      name: 'department',
      label: lang.DEPARTMENT,
      value: contact?.professional?.department,
      render: getValueFromLang,
    },
    {
      name: 'role',
      label: lang.ROLE,
      value: contact?.professional?.role,
      render: getValueFromLang,
      hide: !isUser,
    },
    {
      name: 'levelOfTraining',
      label: lang.LEVEL_OF_TRAINING,
      value: contact?.professional?.levelOfTraining,
      render: getValueFromLang,
      hide: !isUser,
    },
    {
      name: 'rotation',
      label: lang.ROTATION,
      value: contact?.professional?.rotation,
      render: getValueFromLang,
      hide: contact?.professional?.role !== ROLES_DOCTOR.RESIDENT || !isUser,
    },
    {
      name: 'division',
      label: lang.DIVISION,
      value: contact?.professional?.division,
      render: getMultipleValuesFromLang,
      hide: contact?.professional?.role === ROLES_DOCTOR.RESIDENT || !isUser,
    },
    {
      name: 'location',
      label: lang.LOCATION,
      value: contact?.professional?.location,
      render: getLocationsValues,
    },
    {
      name: 'specialty',
      label: lang.SPECIALTY,
      value: contact?.professional?.specialty,
      render: getMultipleValuesFromLang,
      hide: !isUser,
    },
  ];

  const formFieldsOrder = isUser
    ? [
        'email',
        'profession',
        'department',
        'phone',
        'role',
        'rotation',
        'division',
        'cisco',
        'levelOfTraining',
        'location',
        'pager',
        'specialty',
      ]
    : ['email', 'phone', 'pager', 'cisco', 'department', 'location'];

  const deletePopupConfig = {
    [CONTACT_TYPES.USER]: {
      title: contactsLang.DELETE_USER_CONFIRM,
      okText: contactsLang.REDIRECT,
      okType: 'default',
    },
    [CONTACT_TYPES.CONTACT]: {
      title: contactsLang.DELETE_CONFIRM,
      okText: contactsLang.DELETE,
      okType: 'danger',
    },
  };

  const contactType = contact?.isUser ? CONTACT_TYPES.USER : CONTACT_TYPES.CONTACT;

  const renderTopSection = () => (
    <Col span={24} className={styles.topSection}>
      <div className={styles.container}>
        <div className={styles.avatarWrapper}>
          <UserAvatar user={contact} size="large" allowEnlarge />
        </div>
        <div className={styles.header}>
          <Link className={styles.backButton} to={ROUTES.CONTACTS}>
            {contactsLang.TITLE}
          </Link>
          <h1 className={styles.title}>
            {formatFullName(contact, {
              labelInParenthesis: contact?.isDeactivated ? lang.DEACTIVATED : null,
            })}
          </h1>
          <p className={styles.subtitle}>{contact?.professional?.title}</p>
        </div>
      </div>
      {canEditDetails && (
        <div className={styles.buttons}>
          <Popconfirm
            title={deletePopupConfig[contactType].title}
            cancelText={lang.CANCEL}
            okText={deletePopupConfig[contactType].okText}
            okType={deletePopupConfig[contactType].okType}
            okButtonProps={{ type: 'primary' }}
            cancelButtonProps={{ type: 'default' }}
            placement="bottomLeft"
            {...popconfirmProps}>
            <Button type="primary" danger>
              {contactsLang.DELETE}
            </Button>
          </Popconfirm>
          <Button type="primary" onClick={onEdit}>
            {lang.EDIT}
          </Button>
        </div>
      )}
    </Col>
  );

  const renderBottomSection = () => {
    if (!contact?.isUser) return null;

    return <ProfessionalProfileManager user={contact} canEdit={canEditResourcesAndPreferences} />;
  };

  const renderMainSection = () =>
    formFieldsOrder
      .map(field => mainSection.find(f => f.name === field))
      .map(({ label, value, actions = [], render, hide }, index) => {
        if (hide) return null;

        let valueToPresent = render?.(value);
        if (_.isEmpty(valueToPresent)) {
          valueToPresent = '-';
        }

        const empty = valueToPresent === '-';
        const showActions = actions.length > 0 && !empty;

        return (
          <Col
            key={index}
            span={8}
            className={classnames([styles.fieldWrapper, empty && styles.empty])}>
            <p className={styles.label}>{label}</p>
            <div className={styles.row}>
              <p className={styles.value}>{valueToPresent}</p>
              {showActions && (
                <div className={styles.actions}>
                  {actions.map((action, actionIndex) => (
                    <IconButton key={actionIndex} label={action.label} onClick={action.onClick}>
                      <FontAwesomeIcon
                        size="xl"
                        icon={action.icon}
                        color={colorPalette.primaryMain6}
                      />
                    </IconButton>
                  ))}
                </div>
              )}
            </div>
          </Col>
        );
      });

  if (!contact) {
    return <Loader />;
  }

  return (
    <ProfileLayoutComponent
      renderTopSection={renderTopSection}
      renderMainSection={renderMainSection}
      renderBottomSection={renderBottomSection}
    />
  );
};

export { ContactPreviewPage };
